import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Chip,
  Skeleton,
  TableContainer,
  Paper,
  Box,
  Typography,
} from "@mui/material";
import { api } from "../../../../api/api";
import { ILead } from "../../../../redux/leads/leads.types";
import { useBillingCompanyContext } from "../../../../providers/BillingCompanyProvider";
import { useTimezoneFormatter } from "../../../../hooks/useTimezoneFormatter";

interface ISelectEventDialogProps {
  open: boolean;
  multipleEventsUuid: string;
  onClose: () => void;
  onSelect: (leadsUuid: string) => void;
}

export const SelectEventDialog: React.FC<ISelectEventDialogProps> = (props) => {
  const { open, multipleEventsUuid, onClose, onSelect } = props;
  const [events, setEvents] = useState<ILead[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const companyOutlet = useBillingCompanyContext();
  const company_outlet_uuid = companyOutlet.comapny.company_outlet_uuid;
  const { ftDate } = useTimezoneFormatter();

  useEffect(() => {
    if (!open || !multipleEventsUuid) return;

    setLoading(true);
    api
      .get(`/leads/get-leads?company_outlet_uuid=${company_outlet_uuid}&multiple_events_uuid=${multipleEventsUuid}`)
      .then((res: any) => {
        setEvents(res.data?.data || []);
      })
      .catch(() => {
        setEvents([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open, multipleEventsUuid, company_outlet_uuid]);

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case "BOOKED":
        return "success";
      case "CANCELLED":
      case "DEAD":
        return "error";
      case "ONGOING":
      case "WORKING":
        return "primary";
      default:
        return "default";
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Select Event</DialogTitle>
      <DialogContent>
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Event Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Venue</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Event Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton width={120} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={150} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={100} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={80} />
                    </TableCell>
                    <TableCell align="center">
                      <Skeleton variant="rectangular" width={70} height={30} sx={{ mx: "auto" }} />
                    </TableCell>
                  </TableRow>
                ))
              ) : events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Box sx={{ py: 3 }}>
                      <Typography color="text.secondary">No events found.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event: ILead) => (
                  <TableRow key={event.leads_uuid}>
                    <TableCell>{event.event_type}</TableCell>
                    <TableCell>{event.preferred_venue || "-"}</TableCell>
                    <TableCell>{event.function_date ? ftDate(event.function_date) : "-"}</TableCell>
                    <TableCell>
                      <Chip
                        label={event.status || "Unknown"}
                        color={getStatusColor(event.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          if (event.leads_uuid) {
                            onSelect(event.leads_uuid);
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};
